import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { useTranslation } from '../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../shared/hooks/useBusinessNumber.js';
import { DateInput } from '../../../shared/components/DateInput.js';
import { billRefLabel } from '../../../shared/contracts/bill-ref.js';
import { personLabel } from '../../../shared/contracts/approval-trail.js';
import { DataGrid, type DataGridColumn } from './DataGrid.js';
import {
  ledgerQuery,
  type LedgerLine,
  type VendorLedger,
} from '../../../shared/contracts/vendor-ledger.js';

/** A balance as an accountant writes it: the amount, Cr when owed to the vendor. */
export function useBalance(): (n: number) => string {
  const { t } = useTranslation();
  const { money } = useBusinessNumber();
  return (n) => (n === 0 ? money(0) : `${money(Math.abs(n))} ${n > 0 ? t('ledger.cr') : t('ledger.dr')}`);
}

/**
 * The period a ledger is read for. Empty until the first answer, which the
 * server reads as the financial year so far; it then shows the period read.
 */
export function LedgerPeriodForm({
  from,
  to,
  onShow,
}: {
  readonly from: string;
  readonly to: string;
  readonly onShow: (from: string, to: string) => void;
}): ReactNode {
  const { t } = useTranslation();
  const [f, setF] = useState(from);
  const [e, setE] = useState(to);
  useEffect(() => { setF(from); setE(to); }, [from, to]);
  const submit = (ev: FormEvent) => { ev.preventDefault(); onShow(f, e); };
  return (
    <form aria-label="ledger-period" onSubmit={submit} style={{ display: 'flex', gap: 12, alignItems: 'end', margin: '12px 0' }}>
      <label>{t('ledger.from')}<br /><DateInput type="date" value={f} onValueChange={setF} aria-label="ledger-from" /></label>
      <label>{t('ledger.to')}<br /><DateInput type="date" value={e} onValueChange={setE} aria-label="ledger-to" /></label>
      <button type="submit">{t('ledger.show')}</button>
    </form>
  );
}

/** One vendor's account: opening balance, the period's entries, totals and closing balance. */
export function LedgerStatement({ ledger }: { readonly ledger: VendorLedger }): ReactNode {
  const { t } = useTranslation();
  const { day } = useBusinessDay();
  const { money } = useBusinessNumber();
  const balance = useBalance();

  const kindText = (l: LedgerLine) => `${t(`ledger.kind.${l.kind}`)}${l.detail ? ` (${l.detail})` : ''}`;
  const billText = (l: LedgerLine) => (l.bill ? billRefLabel(l.bill, day) : null);
  const particulars = (l: LedgerLine) =>
    [kindText(l), l.invoiceNumber, billText(l)].filter(Boolean).join(' \u00b7 ');
  // The bill number opens the bill itself.
  const particularsCell = (l: LedgerLine) => (
    <>
      {kindText(l)}
      {l.invoiceNumber && (
        <>
          {' \u00b7 '}
          {l.claimId
            ? <Link to={`/billing/claims/${l.claimId}/bill`} data-testid={`ledger-bill-${l.claimId}`}>{l.invoiceNumber}</Link>
            : l.invoiceNumber}
        </>
      )}
      {billText(l) && ` \u00b7 ${billText(l)}`}
    </>
  );

  const columns: DataGridColumn<LedgerLine & { readonly id: string }>[] = [
    { key: 'date', header: t('ledger.col.date'), value: (l) => day(l.date), sortable: false },
    { key: 'particulars', header: t('ledger.col.particulars'), value: particulars, render: particularsCell, sortable: false },
    { key: 'reference', header: t('ledger.col.reference'), value: (l) => l.reference ?? '', sortable: false },
    { key: 'debit', header: t('ledger.col.debit'), value: (l) => l.debit, render: (l) => (l.debit ? money(l.debit) : ''), numeric: true, sortable: false },
    { key: 'credit', header: t('ledger.col.credit'), value: (l) => l.credit, render: (l) => (l.credit ? money(l.credit) : ''), numeric: true, sortable: false },
    { key: 'balance', header: t('ledger.col.balance'), value: (l) => l.balance, render: (l) => balance(l.balance), numeric: true, sortable: false },
  ];
  const rows = ledger.lines.map((l, i) => ({ ...l, id: String(i) }));
  const line = (label: string, value: string, testId: string) => (
    <p style={{ display: 'flex', justifyContent: 'space-between', maxWidth: 520, margin: '4px 0' }}>
      <strong>{label}</strong><span data-testid={testId}>{value}</span>
    </p>
  );

  return (
    <section aria-label="ledger-statement">
      <p style={{ color: 'var(--color-text-muted)' }}>{t('ledger.period', { from: day(ledger.from), to: day(ledger.to) })}</p>
      {line(t('ledger.opening'), balance(ledger.opening), 'ledger-opening')}
      <DataGrid
        caption={t('ledger.entries')}
        columns={columns}
        rows={rows}
        getRowId={(l) => l.id}
        pageSize={25}
        exportFileName={`ledger-${ledger.vendorCode ?? ledger.vendorId}-${ledger.from}-${ledger.to}.csv`}
        emptyMessage={t('ledger.empty')}
      />
      {line(t('ledger.totals'), `${t('ledger.dr')} ${money(ledger.totalDebit)} \u00b7 ${t('ledger.cr')} ${money(ledger.totalCredit)}`, 'ledger-totals')}
      {line(t('ledger.closing'), balance(ledger.closing), 'ledger-closing')}
    </section>
  );
}

/**
 * A vendor's ledger read from `path` (the staff route of one vendor, or the
 * vendor's own), for the period chosen.
 */
export function VendorLedgerView({
  path,
  title,
  note,
  initialFrom = '',
  initialTo = '',
}: {
  readonly path: string;
  /** Headed with the vendor once it is read. */
  readonly title: (vendor: string) => string;
  readonly note?: string;
  readonly initialFrom?: string;
  readonly initialTo?: string;
}): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [period, setPeriod] = useState({ from: initialFrom, to: initialTo });
  const [ledger, setLedger] = useState<VendorLedger | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    apiRequest<VendorLedger>(`${path}${ledgerQuery(period.from, period.to)}`, { token })
      .then(setLedger)
      .catch((e) => setError((e as Error).message));
  }, [path, period, token]);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <h1>{title(ledger ? personLabel(ledger.vendorName, ledger.vendorCode) ?? '' : '')}</h1>
      {note && <p style={{ color: 'var(--color-text-muted)' }}>{note}</p>}
      <LedgerPeriodForm
        from={ledger?.from ?? period.from}
        to={ledger?.to ?? period.to}
        onShow={(from, to) => setPeriod({ from, to })}
      />
      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!ledger && !error && <p>{t('ledger.loading')}</p>}
      {ledger && <LedgerStatement ledger={ledger} />}
    </>
  );
}
